import { BreakdownDisplayMode } from "@redotech/crdb-enums/xborder-outbound/breakdown-display-mode";
import { LandedCostItem } from "@redotech/crdb-enums/xborder-outbound/landed-cost-item";
import { MarketPricingMode } from "@redotech/crdb-enums/xborder-outbound/market-pricing-mode";
import { MarkupStrategy } from "@redotech/crdb-enums/xborder-outbound/markup-strategy";

export type PriceableLandedCostItem =
  | LandedCostItem.Duties
  | LandedCostItem.Taxes;

export interface MarketDefinition {
  markup: number;
  markupStrategy: MarkupStrategy;
  includeInPrice: LandedCostItem[];
  excludeInCheckout: LandedCostItem[];
  coverWithMarkup: LandedCostItem[];
  breakdownDisplayMode?: BreakdownDisplayMode;
  breakdownLabelOverrides?: LandedCostBreakdownLabelOverrides;
}

export interface LandedCostBreakdownLabelOverrides {
  shipping?: string;
  taxes?: string;
  dutiesAndFees?: string;
}

export interface LandedCostAmounts {
  duties: number;
  taxes: number;
  dutyFreeTaxes: number;
  fees: number;
}

export type PriceableAmounts = Pick<LandedCostAmounts, "duties" | "taxes">;

const AMOUNT_KEY: Record<LandedCostItem, keyof LandedCostAmounts> = {
  [LandedCostItem.Duties]: "duties",
  [LandedCostItem.Taxes]: "taxes",
  [LandedCostItem.Fees]: "fees",
};

const PRICEABLE_AMOUNT_KEY: Record<
  PriceableLandedCostItem,
  keyof PriceableAmounts
> = { [LandedCostItem.Duties]: "duties", [LandedCostItem.Taxes]: "taxes" };

export function effectiveMarkup(
  strategy: MarkupStrategy,
  configuredMarkup: number,
  landedFraction: number,
): number {
  switch (strategy) {
    case MarkupStrategy.Absorbing:
      return Math.max(configuredMarkup, landedFraction);
    case MarkupStrategy.Additive:
      return configuredMarkup + landedFraction;
  }
}

function sumIncludedLanded(
  landed: PriceableAmounts,
  includeInPrice: LandedCostItem[],
): number {
  let total = 0;
  for (const item of includeInPrice) {
    if (item === LandedCostItem.Fees) {
      continue;
    }
    total += landed[PRICEABLE_AMOUNT_KEY[item]];
  }
  return total;
}

/** What the vendor is asked about, and what the shopper pays before anything
 * is folded in: base plus the configured markup, whatever the strategy. */
export function exclusivePrice(base: number, markup: number): number {
  return Math.round(base * (1 + markup));
}

export function priceItem(
  base: number,
  landed: PriceableAmounts,
  market: MarketDefinition,
): {
  exclusive: number;
  effectiveMarkup: number;
  markupMargin: number;
  inclusivePrice: number;
} {
  const includedLanded = sumIncludedLanded(landed, market.includeInPrice);
  const landedFraction = base === 0 ? 0 : includedLanded / base;
  const markup = effectiveMarkup(
    market.markupStrategy,
    market.markup,
    landedFraction,
  );
  const exclusive = exclusivePrice(base, market.markup);
  const inclusivePrice = Math.round(base * (1 + markup));
  const markupMargin = inclusivePrice - base - includedLanded;
  return { exclusive, effectiveMarkup: markup, markupMargin, inclusivePrice };
}

export function reversePrice(
  inclusivePrice: number,
  bakedIn: PriceableAmounts,
  market: MarketDefinition,
): { exclusive: number; base: number; markupMargin: number } {
  const includedLanded = sumIncludedLanded(bakedIn, market.includeInPrice);
  let base: number;
  switch (market.markupStrategy) {
    case MarkupStrategy.Additive:
      base = Math.round(
        (inclusivePrice - includedLanded) / (1 + market.markup),
      );
      break;
    case MarkupStrategy.Absorbing: {
      const baseWithinFloor = inclusivePrice / (1 + market.markup);
      base =
        includedLanded <= market.markup * baseWithinFloor
          ? Math.round(baseWithinFloor)
          : inclusivePrice - includedLanded;
      break;
    }
  }
  const exclusive = exclusivePrice(base, market.markup);
  const markupMargin = inclusivePrice - base - includedLanded;
  return { exclusive, base, markupMargin };
}

export type CheckoutLineDisplay = "shown" | "hidden" | "strikethrough";

export interface CheckoutLineItem {
  item: LandedCostItem;
  amount: number;
  originalAmount: number;
  display: CheckoutLineDisplay;
}

export function nonNegativeLandedCost(
  landed: LandedCostAmounts,
): LandedCostAmounts {
  return {
    duties: Math.max(0, landed.duties),
    taxes: Math.max(0, landed.taxes),
    dutyFreeTaxes: Math.max(0, landed.dutyFreeTaxes),
    fees: Math.max(0, landed.fees),
  };
}

export function chargeAtCheckout(
  quotedLanded: LandedCostAmounts,
  markupMargin: number,
  market: MarketDefinition,
): {
  charged: LandedCostAmounts;
  retainedMargin: number;
  lineItems: CheckoutLineItem[];
} {
  const landed = nonNegativeLandedCost(quotedLanded);
  const includeInPrice = new Set<LandedCostItem>(market.includeInPrice);
  const excluded = new Set<LandedCostItem>(market.excludeInCheckout);
  const notCharged = (item: LandedCostItem): boolean =>
    includeInPrice.has(item) || excluded.has(item);
  const taxesOwed = excluded.has(LandedCostItem.Duties)
    ? landed.dutyFreeTaxes
    : landed.taxes;
  const charged: LandedCostAmounts = {
    duties: notCharged(LandedCostItem.Duties) ? 0 : landed.duties,
    taxes: notCharged(LandedCostItem.Taxes) ? 0 : taxesOwed,
    dutyFreeTaxes: notCharged(LandedCostItem.Taxes) ? 0 : landed.dutyFreeTaxes,
    fees: notCharged(LandedCostItem.Fees) ? 0 : landed.fees,
  };

  let budget = markupMargin;
  for (const item of market.coverWithMarkup) {
    const key = AMOUNT_KEY[item];
    // Floor at 0: negative margin covers nothing; merchant absorbs, never claws back from buyer.
    const cover = Math.max(0, Math.min(budget, charged[key]));
    charged[key] -= cover;
    budget -= cover;
  }

  const strikethrough =
    market.breakdownDisplayMode === BreakdownDisplayMode.Strikethrough;
  const lineItems: CheckoutLineItem[] = [
    LandedCostItem.Duties,
    LandedCostItem.Taxes,
    LandedCostItem.Fees,
  ].map((item) => {
    const key = AMOUNT_KEY[item];
    const amount = charged[key];
    const originalAmount = landed[key];
    const uncharged = amount === 0 && originalAmount > 0;
    const display: CheckoutLineDisplay =
      strikethrough && uncharged
        ? "strikethrough"
        : uncharged || excluded.has(item)
          ? "hidden"
          : "shown";
    return { item, amount, originalAmount, display };
  });

  return { charged, retainedMargin: budget, lineItems };
}

export interface MarketDefinitionConflicts {
  coveredButNotCharged: LandedCostItem[];
  includedAndExcluded: LandedCostItem[];
}

export function marketDefinitionConflicts(
  includeInPrice: LandedCostItem[],
  coverWithMarkup: LandedCostItem[],
  excludeInCheckout: LandedCostItem[] = [],
): MarketDefinitionConflicts {
  const excluded = new Set<LandedCostItem>(excludeInCheckout);
  const notCharged = new Set<LandedCostItem>([
    ...includeInPrice,
    ...excludeInCheckout,
  ]);
  return {
    coveredButNotCharged: coverWithMarkup.filter((item) =>
      notCharged.has(item),
    ),
    includedAndExcluded: includeInPrice.filter((item) => excluded.has(item)),
  };
}

export const COVERED_BUT_NOT_CHARGED_MESSAGE =
  "Components already included in price or excluded from checkout cannot " +
  "also be covered by markup";

export const INCLUDED_AND_EXCLUDED_MESSAGE =
  "Components baked into the price cannot also be excluded from checkout";

export function deriveMarketPricingMode(
  includeInPrice: readonly LandedCostItem[],
): MarketPricingMode {
  return includeInPrice.length > 0
    ? MarketPricingMode.Calculated
    : MarketPricingMode.Coefficient;
}

export function inclusionShapeError(
  includeInPrice: LandedCostItem[],
  pricingMode: MarketPricingMode,
): string | null {
  const has = (item: LandedCostItem): boolean => includeInPrice.includes(item);
  switch (pricingMode) {
    case MarketPricingMode.Calculated:
      return has(LandedCostItem.Fees)
        ? "Fees are charged per shipment and cannot be baked into a per-item price"
        : null;
    case MarketPricingMode.Coefficient:
      return includeInPrice.length > 0
        ? "A static coefficient bakes nothing in by declaration; choose calculated or adaptive coefficient to include components in the price"
        : null;
    case MarketPricingMode.AdaptiveCoefficient: {
      const taxOnly =
        has(LandedCostItem.Taxes) &&
        !has(LandedCostItem.Duties) &&
        !has(LandedCostItem.Fees);
      const everything =
        has(LandedCostItem.Taxes) &&
        has(LandedCostItem.Duties) &&
        has(LandedCostItem.Fees);
      return taxOnly || everything
        ? null
        : "Adaptive coefficient supports taxes alone, or duties, taxes and fees together";
    }
  }
}

export function assertValidMarketDefinition(
  market: MarketDefinition,
  pricingMode: MarketPricingMode,
): void {
  const conflicts = marketDefinitionConflicts(
    market.includeInPrice,
    market.coverWithMarkup,
    market.excludeInCheckout,
  );
  if (conflicts.coveredButNotCharged.length > 0) {
    throw new Error(
      `${COVERED_BUT_NOT_CHARGED_MESSAGE}: ${conflicts.coveredButNotCharged.join(", ")}`,
    );
  }
  if (conflicts.includedAndExcluded.length > 0) {
    throw new Error(
      `${INCLUDED_AND_EXCLUDED_MESSAGE}: ${conflicts.includedAndExcluded.join(", ")}`,
    );
  }
  const shapeError = inclusionShapeError(market.includeInPrice, pricingMode);
  if (shapeError) {
    throw new Error(shapeError);
  }
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { BreakdownDisplayMode } from "@redotech/crdb-enums/xborder-outbound/breakdown-display-mode";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { LandedCostItem } from "@redotech/crdb-enums/xborder-outbound/landed-cost-item";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { MarkupStrategy } from "@redotech/crdb-enums/xborder-outbound/markup-strategy";
